import { Component, OnDestroy, OnInit } from '@angular/core'
import { Parking, ParkingStatus } from '@parkupp/core'
import { ParkingService } from 'src/app/services/parking.service'
import { firstValueFrom, Subscription } from 'rxjs'
import { MapTypeStyle } from '@agm/core/services/google-maps-types'
import { EnterpriseContextService } from 'src/app/services/enterprise-context.service'

const PARKING_MAP_VIEW_STATE_KEY = 'parkupp_admin_parking_map_view'

interface StatusConfig {
    label: string
    color: string
    visible: boolean
    count: number
}

@Component({
    selector: 'app-admin-parking-map',
    templateUrl: './admin-parking-map.component.html',
    styleUrls: ['./admin-parking-map.component.scss'],
})
export class AdminParkingMapComponent implements OnInit, OnDestroy {
    cameraLat = -29.320829
    cameraLng = 24.237754
    zoom = 6

    mapStyles: MapTypeStyle[] = [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    ]

    allParkings: Parking[] = []
    displayParkings: Parking[] = []
    loading = true
    selectedParking: Parking | null = null

    statusConfig: { [key: string]: StatusConfig } = {
        [ParkingStatus.PUBLISHED]: { label: 'Published', color: '#22c55e', visible: true, count: 0 },
        [ParkingStatus.PENDING]: { label: 'Pending', color: '#f59e0b', visible: true, count: 0 },
        [ParkingStatus.PRIVATE]: { label: 'Unpublished', color: '#6b7280', visible: true, count: 0 },
        INACTIVE: { label: 'Deleted / Disabled', color: '#ef4444', visible: true, count: 0 },
    }

    statusKeys = Object.keys(this.statusConfig)
    iconUrlMap: { [key: string]: string } = {}
    private enterpriseSubscription: Subscription | null = null

    constructor(
        private parkingService: ParkingService,
        private enterpriseContext: EnterpriseContextService
    ) {
        this.restoreMapView()
    }

    ngOnInit(): void {
        this.enterpriseSubscription = this.enterpriseContext.currentEnterpriseKey$.subscribe(() => {
            this.loadParkings()
        })
    }

    ngOnDestroy(): void {
        this.enterpriseSubscription?.unsubscribe()
        Object.values(this.iconUrlMap).forEach((url) => URL.revokeObjectURL(url))
    }

    private async loadParkings(): Promise<void> {
        this.loading = true
        this.selectedParking = null

        try {
            const parkings = await firstValueFrom(this.parkingService.list())
            this.allParkings = parkings.filter((p) => p.parkingLocation?.latitude && p.parkingLocation?.longitude)
            this.computeCounts()
            this.refreshDisplay()
        } catch (error) {
            console.error('Error loading parking map:', error)
            this.allParkings = []
            this.displayParkings = []
        } finally {
            this.loading = false
        }
    }

    onCenterChange(center: google.maps.LatLngLiteral): void {
        this.cameraLat = center.lat
        this.cameraLng = center.lng
        this.persistMapView()
    }

    onZoomChange(zoom: number): void {
        this.zoom = zoom
        this.persistMapView()
    }

    getEffectiveStatus(parking: Parking): string {
        if (parking.deletedAt) return 'INACTIVE'
        if (parking.parkingStatus === ParkingStatus.DISABLED) return 'INACTIVE'
        if (parking.parkingStatus === ParkingStatus.NEW) return ParkingStatus.PENDING
        return parking.parkingStatus || ParkingStatus.PENDING
    }

    private computeCounts(): void {
        for (const key of this.statusKeys) {
            this.statusConfig[key].count = 0
        }
        for (const parking of this.allParkings) {
            const status = this.getEffectiveStatus(parking)
            if (this.statusConfig[status]) {
                this.statusConfig[status].count++
            }
        }
    }

    toggleStatus(statusKey: string): void {
        this.statusConfig[statusKey].visible = !this.statusConfig[statusKey].visible
        this.refreshDisplay()
    }

    private refreshDisplay(): void {
        this.displayParkings = this.allParkings.filter((p) => {
            const status = this.getEffectiveStatus(p)
            return this.statusConfig[status]?.visible
        })
        this.generateMarkerIcons()
    }

    private generateMarkerIcons(): void {
        Object.values(this.iconUrlMap).forEach((url) => URL.revokeObjectURL(url))
        this.iconUrlMap = {}

        for (const parking of this.displayParkings) {
            if (!parking.$key) continue
            const status = this.getEffectiveStatus(parking)
            const color = this.statusConfig[status]?.color || '#6b7280'
            const svg = this.buildPinSvg(color)
            const blob = new Blob([svg], { type: 'image/svg+xml' })
            this.iconUrlMap[parking.$key] = URL.createObjectURL(blob)
        }
    }

    private buildPinSvg(color: string): string {
        return `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
            <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
            <circle cx="14" cy="14" r="6" fill="#fff"/>
        </svg>`
    }

    onMarkerClick(parking: Parking): void {
        this.selectedParking = parking
    }

    onInfoWindowClose(): void {
        this.selectedParking = null
    }

    getStatusLabel(parking: Parking): string {
        const status = this.getEffectiveStatus(parking)
        return this.statusConfig[status]?.label || status
    }

    getStatusColor(parking: Parking): string {
        const status = this.getEffectiveStatus(parking)
        return this.statusConfig[status]?.color || '#6b7280'
    }

    getAddress(parking: Parking): string {
        const loc = parking.parkingLocation
        if (!loc) return 'No address'
        const parts = [loc.suburb, loc.city, loc.province].filter(Boolean)
        return parts.length ? parts.join(', ') : 'No address'
    }

    getMerchantKey(parking: Parking): string | null {
        return parking.merchantRef?.id || null
    }

    private restoreMapView(): void {
        try {
            const saved = localStorage.getItem(PARKING_MAP_VIEW_STATE_KEY)
            if (!saved) return

            const parsed = JSON.parse(saved)
            if (typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
                this.cameraLat = parsed.lat
                this.cameraLng = parsed.lng
            }
            if (typeof parsed.zoom === 'number') {
                this.zoom = parsed.zoom
            }
        } catch (error) {
            console.warn('Failed to restore parking map view:', error)
        }
    }

    private persistMapView(): void {
        try {
            localStorage.setItem(
                PARKING_MAP_VIEW_STATE_KEY,
                JSON.stringify({
                    lat: this.cameraLat,
                    lng: this.cameraLng,
                    zoom: this.zoom,
                })
            )
        } catch (error) {
            console.warn('Failed to persist parking map view:', error)
        }
    }

    get totalVisible(): number {
        return this.displayParkings.length
    }

    get totalAll(): number {
        return this.allParkings.length
    }
}
